import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import ClientGuideAnimated from "../components/ClientGuideAnimated";

const VideoDetect = () => {
  const MAX_SIZE = 100 * 1024 * 1024;

  const [video, setVideo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const { t } = useTranslation();
  const inputRef = useRef();

  const handleFile = (file) => {
    if (!file) return;

    if (file.size > MAX_SIZE) {
      setError("La taille maximale autorisée est 100 MB");
      return;
    }

    setError("");
    setVideo(file);
    setPreview(URL.createObjectURL(file));
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = () => {
    if (!video) return;

    const formData = new FormData();
    formData.append("video", video);

    setLoading(true);
    setProgress(0);
    setError("");
    setResult(null);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "https://mydatacheck.site/api/detect-video");

    const startTime = Date.now();

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = (event.loaded / event.total) * 100;
        setProgress(Math.round(percent));

        const elapsed = (Date.now() - startTime) / 1000;
        const uploadSpeed = event.loaded / elapsed;
        const remainingBytes = event.total - event.loaded;
        const remainingSeconds = remainingBytes / uploadSpeed;

        setRemainingTime(Math.round(remainingSeconds));
      }
    };

    xhr.onload = () => {
      try {
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.response);

          // Sécuriser le résultat
          if (data && typeof data === "object") {
            setResult({
              video: data.video || null,
              audio: data.audio || null,
            });
          } else {
            setResult({ video: null, audio: null });
            console.warn("Réponse inattendue :", data);
          }
        } else {
          // Gérer les erreurs HTTP
          let errorMsg = `Erreur ${xhr.status}`;
          try {
            const errorData = JSON.parse(xhr.response);

            if (errorData.error) {
              errorMsg =
                typeof errorData.error === "string"
                  ? errorData.error
                  : JSON.stringify(errorData.error);
            } else if (errorData.message) {
              errorMsg =
                typeof errorData.message === "string"
                  ? errorData.message
                  : JSON.stringify(errorData.message);
            }
          } catch (e) {
            // Réponse non JSON → on garde le message par défaut
          }
          setError(errorMsg);
        }
      } catch (e) {
        setError("Erreur de traitement de la réponse");
        console.error("Parsing error:", e);
      } finally {
        setLoading(false);
        setRemainingTime(null);
      }
    };

    xhr.onerror = () => {
      setError("Erreur lors de l'envoi");
      setLoading(false);
    };

    xhr.send(formData);
  };

  return (
    <div className="bg-gray-100 p-6">
      <div className="min-h-screen flex items-center  gap-2 justify-center bg-gray-100 p-6">
        <div className="w-full max-w-2xl bg-white rounded-xl shadow-lg p-8 space-y-6">
          <h1 className="text-2xl font-bold text-center text-[#42AB3F]">
            {t("videoheade")}
          </h1>

          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-orange-500 transition"
          >
            <p className="text-gray-600">{t("videoheade1")}</p>

            <p className="text-sm text-gray-400 mt-2">{t("videoheade2")}</p>
            <span>{t("videoheade3")} </span>
            <input
              ref={inputRef}
              type="file"
              accept="video/*"
              onChange={(e) => handleFile(e.target.files[0])}
              className="hidden"
            />
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {preview && (
            <video src={preview} controls className="w-full rounded-lg" />
          )}

          {video && (
            <button
              onClick={handleUpload}
              disabled={loading}
              className="w-full bg-orange-500  text-white py-3 rounded-lg hover:bg-orange-700 transition"
            >
              {loading ? t("videoheade4") : t("videoheade5")}
            </button>
          )}

          {loading && (
            <div className="space-y-3">
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="h-4 bg-orange-600 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="text-sm text-gray-600 text-center">
                Upload : {progress}%
              </div>

              {remainingTime !== null && (
                <div className="text-sm text-gray-500 text-center">
                  {t("videoheade6")}: {remainingTime} secondes
                </div>
              )}
            </div>
          )}

          {result && (
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="border rounded-lg p-4 bg-gray-50">
                <h2 className="font-semibold text-gray-700">Vidéo</h2>
                <p className="text-gray-600">
                  Label :{" "}
                  <span className="ml-2 font-bold">
                    {result.video?.label || "N/A"}
                  </span>
                </p>
                <p className="text-gray-600">
                  Confiance :{" "}
                  <span className="ml-2 font-bold">
                    {result.video?.confidence
                      ? (result.video.confidence * 100).toFixed(2) + "%"
                      : "N/A"}
                  </span>
                </p>
              </div>

              {/* Audio */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h2 className="font-semibold text-gray-700">Audio</h2>
                <p className="text-gray-600">
                  Label :{" "}
                  <span className="ml-2 font-bold">
                    {result.audio?.label || "N/A"}
                  </span>
                </p>
                <p className="text-gray-600">
                  Confiance :{" "}
                  <span className="ml-2 font-bold">
                    {result.audio?.confidence
                      ? (result.audio.confidence * 100).toFixed(2) + "%"
                      : "N/A"}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoDetect;
